import React, { useEffect, useMemo } from 'react';
import { Form, Input, Typography, Card, Empty, Tag } from 'antd';
import { FileExcelOutlined, DatabaseOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ExportExcelNodeProperties = ({ config, updateConfig, nodes = [], edges = [], selectedNode }) => {
    const [form] = Form.useForm();

    // Enforce single handle mode by clearing sheets config
    useEffect(() => {
        if (config.sheets && config.sheets.length > 0) {
            updateConfig({
                config: {
                    ...config,
                    sheets: [] // Clear sheets to revert to default 'dataset' handle
                }
            });
        }
    }, [config.sheets, updateConfig]);

    useEffect(() => {
        form.setFieldsValue({
            fileName: config.fileName || 'export_data',
        });
    }, [config, form]);

    const handleValuesChange = (changedValues, allValues) => {
        if (updateConfig) {
            updateConfig({
                config: {
                    ...config,
                    ...allValues
                }
            });
        }
    };

    // Get all connected source nodes
    const connectedSources = useMemo(() => {
        if (!selectedNode || !edges) return [];
        
        // Find all edges connected to this node's target handles
        const connectedEdges = edges.filter(e => e.target === selectedNode.id);
        
        return connectedEdges.map(edge => {
            const sourceNode = nodes.find(n => n.id === edge.source);
            if (!sourceNode) return null;
            
            const nodeConfig = sourceNode.data?.config || {};
            // Prioritize explicit dataset name, then table name, then node label
            const datasetName = nodeConfig.datasetName || nodeConfig.tableName || nodeConfig.fileName || sourceNode.data?.customName || sourceNode.data?.label || 'Untitled Dataset';
            
            return {
                id: sourceNode.id,
                name: datasetName,
                type: sourceNode.data?.nodeType
            };
        }).filter(Boolean);
    }, [nodes, edges, selectedNode]);

    // Sync sheet count to config for node label display
    useEffect(() => {
        if (config.sheetCount !== connectedSources.length) {
            updateConfig({
                config: {
                    ...config,
                    sheetCount: connectedSources.length
                }
            });
        }
    }, [connectedSources.length, config.sheetCount, config, updateConfig]);

    return (
        <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
            <div style={{ marginBottom: 16, padding: '12px', background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <FileExcelOutlined /> Xuất dữ liệu ra file Excel.
                </Text>
            </div>

            <Form.Item 
                name="fileName" 
                label="File Name" 
                rules={[{ required: true }]}
            >
                <Input addonAfter=".xlsx" placeholder="report_2023" />
            </Form.Item>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>Connected Sheets (Read-only):</Text>
            
            {connectedSources.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {connectedSources.map((source, index) => (
                        <Card 
                            key={source.id} 
                            size="small" 
                            bodyStyle={{ padding: '8px 12px' }}
                            style={{ background: '#fafafa' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <DatabaseOutlined style={{ color: '#1890ff' }} />
                                    <div>
                                        <Text strong style={{ fontSize: 13 }}>{source.name}</Text>
                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                            Source: {source.type}
                                        </div>
                                    </div>
                                </div>
                                <Tag color="blue">Sheet {index + 1}</Tag>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <Empty 
                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                    description={<span style={{ fontSize: 12, color: '#999' }}>Connect datasets to this node to add sheets</span>} 
                />
            )}
            
            <div style={{ marginTop: 12, fontSize: 11, color: '#8c8c8c', fontStyle: 'italic' }}>
                * Sheet names are automatically synced from connected datasets. Use "Set Dataset Name" node to rename.
            </div>
        </Form>
    );
};

export default ExportExcelNodeProperties;